import { PageHeader, FilterBar, LoadingState, DataTable, OverflowActions, EmptyState, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  useMediaQuery, alpha, useTheme, TablePagination, TextField, InputAdornment
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Phone, Email, Group, Female, Male, CloudUpload, FileDownload, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';
import StudentForm from '../components/StudentForm';
import BulkStudentModal from '../components/BulkStudentModal';
import BulkPictureModal from '../components/BulkPictureModal';
import BulkContactModal from '../components/BulkContactModal';

const StudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkPictureModal, setShowBulkPictureModal] = useState(false);
  const [showBulkContactModal, setShowBulkContactModal] = useState(false);
  const [exportingContacts, setExportingContacts] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [globalStats, setGlobalStats] = useState({ total: 0, male: 0, female: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Get schoolId from user
  const schoolId = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;
      const user = JSON.parse(raw);
      const extracted = user.school?._id || user.school?.id || user.school;
      return typeof extracted === 'object' ? null : extracted;
    } catch (err) {
      console.error("Failed to read user from localStorage:", err);
      return null;
    }
  })();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get('/students', {
        params: {
          excludeGraduated: true,
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined
        }
      });
      if (res.data && res.data.students) {
        setStudents(res.data.students.sort((a, b) => (a.user?.name || '').localeCompare(b.user?.name || '')));
        setTotalCount(res.data.totalStudents);
        setGlobalStats({
          total: res.data.totalStudents,
          male: res.data.maleCount,
          female: res.data.femaleCount
        });
      } else {
        const data = Array.isArray(res.data) ? res.data : [];
        const sortedData = data.sort((a, b) => (a.user?.name || '').localeCompare(b.user?.name || ''));
        setStudents(sortedData);
        setTotalCount(sortedData.length);
        setGlobalStats({
          total: sortedData.length,
          male: sortedData.filter(s => s.gender === 'Male').length,
          female: sortedData.filter(s => s.gender === 'Female').length
        });
      }
    } catch (err) {
      console.error('❌ Error fetching students:', err);
      toast.error('Failed to fetch students');
    }
  }, [page, rowsPerPage, debouncedSearch]);

  const downloadContacts = async () => {
    setExportingContacts(true);
    try {
      const { data } = await api.get('/students/contacts/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student-contacts-whole-school-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Whole-school contacts Excel downloaded. Fill in the highlighted phone columns.');
    } catch (err) {
      let message = 'Unable to download student contacts. Please try again.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try { message = JSON.parse(await data.text()).message || message; } catch { }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setExportingContacts(false);
    }
  };

  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get('/classes');
      const responseData = res.data;
      if (Array.isArray(responseData)) {
        setClasses(responseData);
      } else if (Array.isArray(responseData.classes)) {
        setClasses(responseData.classes);
      } else {
        console.error('⚠️ Unexpected classes format:', responseData);
        setClasses([]);
      }
    } catch (err) {
      console.error('❌ Error fetching classes:', err);
      toast.error('Failed to load classes');
      setClasses([]);
    }
  }, []);

  const fetchAcademicYears = useCallback(async () => {
    try {
      if (!schoolId) {
        console.warn('School ID not available');
        return;
      }

      const [yearsRes, allTermsRes] = await Promise.all([
        api.get('/term/years', { params: { school: schoolId } }),
        api.get('/term/all', { params: { school: schoolId } }).catch(() => null)
      ]);

      const years = yearsRes.data?.academicYears || [];
      const termsData = allTermsRes?.data?.allTerms || [];
      const activeTerm = termsData.find(t => t.isCurrent);
      const activeYear = activeTerm ? activeTerm.academicYear : null;

      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => {
          if (a === activeYear) return -1;
          if (b === activeYear) return 1;
          return b.localeCompare(a);
        });
        setAcademicYears(sortedYears);
      } else {
        console.warn('No academic years found');
        setAcademicYears([]);
      }
    } catch (err) {
      console.error('❌ Error fetching academic years:', err);
      toast.error('Failed to load academic years');
      setAcademicYears([]);
    }
  }, [schoolId]);

  // Helper to scroll to element
  const scrollToElement = (id) => {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.transition = 'background-color 2s';
        element.style.backgroundColor = alpha(theme.palette.primary.main, 0.2);
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      }
    }, 500); // Delay to ensure rendering
  };

  const initialLoadDone = React.useRef(false);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        if (!initialLoadDone.current) {
          setFetching(true);
        }
        await Promise.all([fetchClasses(), fetchAcademicYears()]);

        // Check for search param after data load
        const params = new URLSearchParams(window.location.search);
        const searchId = params.get('search');
        if (searchId) {
          // Keep existing comments
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setFetching(false);
        initialLoadDone.current = true;
      }
    };
    fetchMetadata();
  }, [fetchClasses, fetchAcademicYears]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Handle Deep Linking / Search highlighting
  useEffect(() => {
    if (!fetching) {
      const params = new URLSearchParams(window.location.search);
      const searchTerm = params.get('search');
      if (searchTerm && !searchQuery) {
          setSearchQuery(searchTerm);
      }
    }
  }, [fetching]);

  // Stats calculation
  const totalStudents = globalStats.total;
  const maleStudents = globalStats.male;
  const femaleStudents = globalStats.female;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = useCallback((student) => {
    setEditingStudent(student);
    setShowStudentForm(true);
  }, []);

  const handleFormSuccess = useCallback(() => {
    fetchStudents();
    setEditingStudent(null);
    setShowStudentForm(false);
  }, [fetchStudents]);

  const handleFormCancel = useCallback(() => {
    setEditingStudent(null);
    setShowStudentForm(false);
  }, []);

  const handleDeleteClick = useCallback((student) => {
    setStudentToDelete(student);
    setShowConfirmDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!studentToDelete) return;

    try {
      await api.delete(`/students/${studentToDelete._id}`);
      toast.success('Student deleted successfully');
      fetchStudents();
    } catch (err) {
      console.error('❌ Error deleting student:', err);
      toast.error('Failed to delete student');
    } finally {
      setShowConfirmDialog(false);
      setStudentToDelete(null);
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

  const handleOpenProfile = (student) => {
    setSelectedStudent(student);
    setProfileOpen(true);
  };

  if (fetching) {
    return (
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Student Management</>} description={<>Manage student records and enrollment</>} actions={<>
        <Tooltip title="Download every student in your school, including all classes and pages">
                    <span style={{
            width: isMobile ? '100%' : undefined
          }}>
                      <Button variant="outlined" startIcon={exportingContacts ? <CircularProgress size={18} color="inherit" /> : <FileDownload />} onClick={downloadContacts} disabled={exportingContacts} fullWidth={isMobile}>
                        {exportingContacts ? 'Downloading...' : 'Download Contacts Excel'}
                      </Button>
                    </span>
                  </Tooltip>
        <OverflowActions><Button variant="outlined" color="primary" startIcon={<CloudUpload />} onClick={() => setShowBulkContactModal(true)} fullWidth={isMobile}>
                    Upload Contacts Excel
                  </Button>
          <Button variant="outlined" color="primary" startIcon={<CloudUpload />} onClick={() => setShowBulkModal(true)} fullWidth={isMobile}>
                    Bulk Onboarding
                  </Button>
          <Button variant="outlined" color="secondary" startIcon={<CloudUpload />} onClick={() => setShowBulkPictureModal(true)} fullWidth={isMobile}>
                    Bulk Pictures
                  </Button></OverflowActions>


        {!showStudentForm && <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={() => setShowStudentForm(true)} fullWidth={isMobile}>
                      Add Student
                    </Button>}
      </>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.primary.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Group fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
                    Total Students
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
                    {totalStudents}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                    Enrolled students
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.info.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.info.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Male fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
                    Male Students
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
                    {maleStudents}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                    {totalStudents > 0 ? `${((maleStudents / totalStudents) * 100).toFixed(1)}%` : '0%'} of total
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.secondary.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Female fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
                    Female Students
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
                    {femaleStudents}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                    {totalStudents > 0 ? `${((femaleStudents / totalStudents) * 100).toFixed(1)}%` : '0%'} of total
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>
      <FilterBar>
<TextField label="Search students"
              size="small"
              placeholder="Search student..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ width: { xs: '100%', sm: 320 } }}
            />
      </FilterBar>

      {/* Stats Cards */}
      

      {/* Student Form */}
      <StudentForm
        open={showStudentForm}
        onToggle={() => setShowStudentForm(prev => !prev)}
        editingStudent={editingStudent}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
        classes={classes}
        academicYears={academicYears}
      />

      <BulkContactModal
        open={showBulkContactModal}
        onClose={() => setShowBulkContactModal(false)}
        onSuccess={fetchStudents}
      />

      <BulkStudentModal
        open={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={fetchStudents}
        classes={classes}
        academicYears={academicYears}
      />

      <BulkPictureModal
        open={showBulkPictureModal}
        onClose={() => setShowBulkPictureModal(false)}
        onSuccess={fetchStudents}
      />

      {/* Students List */}
      {!isMobile ? (
        <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
            <DataTable><Table>
              <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>STUDENT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>CLASS</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>GENDER</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>GUARDIAN</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
{students.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                {students.map((student) => (
                  <TableRow
                    key={student._id}
                    id={student._id}
                    sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }, transition: 'background-color 0.5s' }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <StudentAvatar student={student} sx={{ bgcolor: theme.palette.primary.main }}>{getInitials(student.user?.name)}</StudentAvatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            onClick={() => handleOpenProfile(student)}
                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                          >
                            {student.user?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{student.user?.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{student.class?.displayName || student.class?.name || 'Unassigned'}</TableCell>
                    <TableCell>
                      <Chip label={student.gender} size="small" color={student.gender === 'Male' ? 'primary' : 'secondary'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        {student.fatherName && <Typography variant="body2" sx={{ fontWeight: '500' }}>Father: {student.fatherName}</Typography>}
                        {student.motherName && <Typography variant="body2" sx={{ fontWeight: '500' }}>Mother: {student.motherName}</Typography>}
                        {(student.guardianPhone || student.guardianPhone2) && (
                           <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                             {student.guardianPhone && (
                               <Box 
                                   component="a" 
                                   href={`tel:${student.guardianPhone}`} 
                                   sx={{ 
                                       color: 'primary.main', 
                                       textDecoration: 'none', 
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: 0.5,
                                       fontSize: '0.8125rem',
                                       fontWeight: '500',
                                       '&:hover': { textDecoration: 'underline' } 
                                   }}
                               >
                                   <Phone sx={{ fontSize: '0.875rem' }} /> {student.guardianPhone}
                               </Box>
                             )}
                             {student.guardianPhone2 && (
                               <Box 
                                   component="a" 
                                   href={`tel:${student.guardianPhone2}`} 
                                   sx={{ 
                                       color: 'primary.main', 
                                       textDecoration: 'none', 
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: 0.5,
                                       fontSize: '0.8125rem',
                                       fontWeight: '500',
                                       '&:hover': { textDecoration: 'underline' } 
                                   }}
                               >
                                   <Phone sx={{ fontSize: '0.875rem' }} /> {student.guardianPhone2}
                               </Box>
                             )}
                           </Stack>
                        )}
                        {!student.fatherName && !student.motherName && !student.guardianPhone && (
                           <Typography variant="caption" color="text.secondary">No guardian details</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(student)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(student)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></DataTable>
          </Box>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Card>
      ) : (
        <Box sx={{ p: 0 }}>
          {/* Mobile Search Bar since hidden in header */}
          
          {students.length === 0 && <EmptyState title="No students found" description="Try another search or add a new record using the actions above." />}
{students.map((student) => (
            <MobileStudentCard
              key={student._id}
              student={student}
              theme={theme}
              handleEdit={handleEdit}
              handleDeleteClick={handleDeleteClick}
              handleOpenProfile={handleOpenProfile}
            />
          ))}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
          />
        </Box>
      )}

      {/* Student Profile Dialog */}
      <Dialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <StudentAvatar student={selectedStudent} sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
              {selectedStudent ? getInitials(selectedStudent.user?.name) : 'S'}
            </StudentAvatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">{selectedStudent?.user?.name}</Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={selectedStudent?.admissionNumber || 'No Adm. No.'} size="small" color="primary" variant="outlined" />
                {selectedStudent?.gender && (
                  <Chip label={selectedStudent.gender} size="small" color={selectedStudent.gender === 'Male' ? 'info' : 'secondary'} variant="outlined" />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={() => setProfileOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedStudent && (
            <Stack spacing={3}>
              {/* Section 1 — Student Info */}
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>STUDENT INFORMATION</Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">EMAIL</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.user?.email || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">DATE OF BIRTH</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {selectedStudent.dateOfBirth ? new Date(selectedStudent.dateOfBirth).toLocaleDateString() : '—'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">CLASS</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.class?.displayName || selectedStudent.class?.name || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">ACADEMIC YEAR</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.academicYear || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">RELIGION</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.religion || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">HOME TOWN</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.hometown || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">LANGUAGE SPOKEN</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.languageSpoken || '—'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Section 2 — Parent/Guardian Info */}
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>PARENT / GUARDIAN INFORMATION</Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">FATHER / GUARDIAN NAME</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.fatherName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">FATHER'S OCCUPATION</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.fatherOccupation || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">MOTHER / GUARDIAN NAME</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.motherName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">MOTHER'S OCCUPATION</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedStudent.motherOccupation || '—'}</Typography>
                  </Grid>
                  {selectedStudent.guardianOccupation && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">GUARDIAN OCCUPATION</Typography>
                      <Typography variant="body2" fontWeight="500">{selectedStudent.guardianOccupation}</Typography>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">PRIMARY CONTACT (PHONE 1)</Typography>
                    {selectedStudent.guardianPhone ? (
                      <Box
                        component="a"
                        href={`tel:${selectedStudent.guardianPhone}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}
                      >
                        <Phone fontSize="small" /> {selectedStudent.guardianPhone}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>None</Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">SECONDARY CONTACT (PHONE 2)</Typography>
                    {selectedStudent.guardianPhone2 ? (
                      <Box
                        component="a"
                        href={`tel:${selectedStudent.guardianPhone2}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold', '&:hover': { textDecoration: 'underline' } }}
                      >
                        <Phone fontSize="small" /> {selectedStudent.guardianPhone2}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>None</Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, m: isMobile ? 0 : 'auto' } }}>
        <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon /> Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, mt: 2 }}>
          <Typography>Are you sure you want to delete student <strong>{studentToDelete?.user?.name}</strong>?</Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>This action cannot be undone.</Alert>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setShowConfirmDialog(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Mobile Student Card outside
const MobileStudentCard = React.memo(({ student, theme, handleEdit, handleDeleteClick, handleOpenProfile }) => {
  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';
  const getGenderColor = (gender) => gender === 'Male' ? 'primary' : 'secondary';
  const getGenderIcon = (gender) => gender === 'Male' ? <Male /> : <Female />;

  return (
    <Card sx={{ mb: 2, boxShadow: 2, transition: 'background-color 0.5s' }} id={student._id}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <StudentAvatar student={student} sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>{getInitials(student.user?.name)}</StudentAvatar>
              <Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight="600"
                  onClick={() => handleOpenProfile && handleOpenProfile(student)}
                  sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                >
                  {student.user?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">{student.class?.displayName || student.class?.name || 'No class'}</Typography>
              </Box>
            </Stack>
            <Chip label={student.gender} size="small" color={getGenderColor(student.gender)} variant="outlined" icon={getGenderIcon(student.gender)} />
          </Stack>
          <Stack spacing={1}>
            <Box display="flex" alignItems="center" gap={1}><Email fontSize="small" color="action" /><Typography variant="body2" noWrap>{student.user?.email}</Typography></Box>
            {student.guardianPhone && (
                <Box 
                    display="flex" 
                    alignItems="center" 
                    gap={1} 
                    component="a" 
                    href={`tel:${student.guardianPhone}`}
                    sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <Phone fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: '500' }}>{student.guardianPhone}</Typography>
                </Box>
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>DATE OF BIRTH</Typography>
              <Typography variant="body2">{student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>ADMISSION NO.</Typography>
              <Typography variant="body2">{student.admissionNumber || '—'}</Typography>
            </Box>
          </Stack>
          {(student.fatherName || student.motherName || student.guardianPhone) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>GUARDIANS</Typography>
              {student.fatherName && <Typography variant="body2" fontWeight="500">Father: {student.fatherName}</Typography>}
              {student.motherName && <Typography variant="body2" fontWeight="500">Mother: {student.motherName}</Typography>}
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>ACADEMIC YEAR</Typography>
            <Chip label={student.academicYear || 'N/A'} size="small" color="secondary" variant="outlined" />
          </Box>
          {(student.religion || student.hometown || student.languageSpoken) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>OTHER DETAILS</Typography>
              <Typography variant="body2">
                {student.religion && `Religion: ${student.religion}`}
                {student.hometown && ` • Home: ${student.hometown}`}
                {student.languageSpoken && ` • Lang: ${student.languageSpoken}`}
              </Typography>
            </Box>
          )}
          <ActionToolbar direction="row" spacing={1} justifyContent="space-between">
            <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => handleEdit(student)} sx={{ flex: 1 }}>Edit</Button>
            <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteClick(student)} sx={{ flex: 1 }}>Delete</Button>
          </ActionToolbar>
        </Stack>
      </CardContent>
    </Card>
  );
});

export default StudentsPage;